import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import api from '../config/api';
import toast from 'react-hot-toast';
import { getFCMToken } from '../config/firebase';

const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const ACTIVITY_CHECK_INTERVAL_MS = 60 * 1000;  // check every minute

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: 'admin' | 'agent' | 'sales' | 'sales_agent' | 'sales_rep_internal' | 'developer' | 'resellers' | 'agent_partner' | 'sales_rep_associated';
  phone?: string;
  extension?: string;
  ringcentral_extension?: string;
  is_active: boolean;
  last_login?: string;
  created_at: string;
  updated_at: string;
  avatar_url?: string;
  email_needs_update?: boolean;
  email_update_required?: boolean;
  email_migration_dismiss_count?: number;
  /** Role-based permissions from roles.permissions (JSONB) */
  permissions?: Record<string, boolean>;
  /** Legacy - backward compatibility during migration */
  can_edit_businesses?: boolean;
  can_manage_replacements?: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
  refetchUser: (options?: { skipCache?: boolean }) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const lastActivityRef = useRef<number>(Date.now());

  useEffect(() => {
    // Skip auth check if we're on the TV Dashboard route (it has its own auth)
    if (window.location.pathname === '/tvdashboard' || window.location.pathname.startsWith('/tvdashboard')) {
      setLoading(false);
      return;
    }

    const token = localStorage.getItem('token');
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await api.get('/auth/me');
      console.log('Auth /me URL:', (response as any)?.request?.responseURL || 'n/a');
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  };

  const refetchUser = async (options?: { skipCache?: boolean }) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const url = options?.skipCache ? `/auth/me?_=${Date.now()}` : '/auth/me';
      const response = await api.get(url);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to refetch user:', error);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      console.log('Auth /login URL:', (response as any)?.request?.responseURL || 'n/a');
      const { token, user: userData, requires_password_change, message } = response.data;

      if (requires_password_change) {
        const passwordChangeError: any = new Error(message || 'Password change required before login');
        passwordChangeError.code = 'PASSWORD_CHANGE_REQUIRED';
        passwordChangeError.email = response.data?.email || email;
        throw passwordChangeError;
      }
      
      localStorage.setItem('token', token);
      lastActivityRef.current = Date.now();
      setUser(userData);
      // Refetch user from /me (skip cache) so email_needs_update and dismiss count are guaranteed
      await refetchUser({ skipCache: true });
      
      toast.success(`Welcome back, ${userData.first_name}!`);
    } catch (error: any) {
      // Don't show toast here - let the Login component handle it
      // Just throw the error so it can be caught and displayed properly
      throw error;
    }
  };

  const logout = () => {
    // Unregister FCM token before clearing auth (API requires auth)
    getFCMToken()
      .then((token) => {
        if (token) {
          return api.delete('/api/chat/device-token', { data: { token } });
        }
      })
      .catch(() => {})
      .finally(() => {
        localStorage.removeItem('token');
        setUser(null);
        toast.success('Logged out successfully');
      });
  };

  // Session timeout: log out after 30 minutes of inactivity (only when logged in, not on TV Dashboard or Chat)
  useEffect(() => {
    const onTvDashboard = window.location.pathname === '/tvdashboard' || window.location.pathname.startsWith('/tvdashboard');
    const onChat = window.location.pathname === '/chat' || window.location.pathname.startsWith('/chat');
    if (!user || onTvDashboard || onChat) {
      return;
    }
    lastActivityRef.current = Date.now();

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const updateActivity = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        lastActivityRef.current = Date.now();
        debounceTimer = null;
      }, 1000);
    };
    events.forEach((ev) => window.addEventListener(ev, updateActivity));

    const intervalId = setInterval(() => {
      const onTvDashboard = window.location.pathname === '/tvdashboard' || window.location.pathname.startsWith('/tvdashboard');
      const onChat = window.location.pathname === '/chat' || window.location.pathname.startsWith('/chat');
      if (onTvDashboard || onChat) return; // TV Dashboard and Chat are excluded from session timeout
      if (Date.now() - lastActivityRef.current >= INACTIVITY_TIMEOUT_MS) {
        clearInterval(intervalId);
        events.forEach((ev) => window.removeEventListener(ev, updateActivity));
        localStorage.removeItem('token');
        setUser(null);
        toast.success('Logged out due to inactivity');
      }
    }, ACTIVITY_CHECK_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
      events.forEach((ev) => window.removeEventListener(ev, updateActivity));
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [user]);

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      setUser({ ...user, ...userData });
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    logout,
    updateUser,
    refetchUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
